import type { Course, Entity, EntityDict, ModuleBuild, PlayerBuild, UserCoursePerformance } from '@webapp2/shared/model'
import { capitalize, toRaw } from 'vue'
import {
  state,
  type ModuleBuildRegistrationState,
  type MultiplayerSession,
  type PlayerTechnique,
  type Prebrief,
  type State
} from '../state'
import { postGetBody } from './api'
import { ensureModuleBuilds, registerModulesForTechniques } from './build-service'
import { cache } from './cache'
import { ensureMultiplayerSessionDependencies } from './multiplayer-session-service'
import { registerModules } from './quest-bridge'

let initialUpdateResolve: () => void
const etags: { [id: string]: string | undefined } = {}

export const initialUpdate = new Promise<void>(resolve => (initialUpdateResolve = resolve))

export async function setStateFromCache() {
  const [
    organizationId,
    moduleBuilds,
    courses,
    multiplayerSessions,
    playerBuilds,
    playerTechniques,
    prebriefs,
    registeredBuilds,
    userCoursePerformances
  ] = await Promise.all([
    cache.get<string>('currentOrganizationId'),
    cache.getByType<ModuleBuild>('ModuleBuild'),
    cache.getByType<Course>('course'),
    cache.getByType<MultiplayerSession>('multiplayerSession'),
    cache.getByType<PlayerBuild>('playerBuild'),
    cache.getByType<PlayerTechnique>('playerTechnique'),
    cache.getByType<Prebrief>('prebrief'),
    cache.getByType<ModuleBuildRegistrationState>('RegisteredState'),
    cache.getByType<UserCoursePerformance>('UserCoursePerformance')
  ])

  state.organizationId = organizationId

  state.moduleBuilds = moduleBuilds
  state.courses = courses
  state.multiplayerSessions = multiplayerSessions
  state.playerBuilds = playerBuilds
  state.playerTechniques = playerTechniques
  state.prebriefs = prebriefs
  state.userCoursePerformances = userCoursePerformances
  state.registeredStates = registeredBuilds

  const overriddenModuleBuilds = Object.values(state.registeredStates).filter(x => x.overrideId)
  await ensureModuleBuilds(overriddenModuleBuilds.map(x => x.overrideId!))

  if (overriddenModuleBuilds.length > 0) {
    registerModules({
      builds: overriddenModuleBuilds.map(x => state.moduleBuilds[x.overrideId!]!)
    })
  }
}

export function updateEntities(loadAll = false) {
  return updateByTypes(Object.keys(typeToStateKey) as EntityType[], loadAll)
}

function getByTypes(types: string[], loadAll: boolean) {
  return postGetBody<DocumentsByOrganizationResponse<EntityDict<Entity>>>(
    `organization/${state.organizationId}/documents`,
    types.map(x => {
      return { type: capitalize(x), etag: loadAll ? undefined : etags[x] }
    })
  )
}

export async function updateByTypes<K extends EntityType>(types: K[], loadAll = false): Promise<void> {
  const response = await getByTypes(types, loadAll)
  if (!response) {
    console.log(`Failed to update via API using cache for type: ${types.join(', ')}`)
    return
  }

  if (response.course?.documents) {
    const buildIds = Object.values(response.course.documents as EntityDict<Course>).map(x => x.buildId)
    await ensureModuleBuilds(buildIds)
  }

  const { cacheItems, etagsByType } = types.reduce(
    (acc, type) => {
      const stateKey = typeToStateKey[type]
      const documentSet = response[type]
      const docs = (documentSet?.documents ?? {}) as State[typeof stateKey]

      if (loadAll) {
        state[stateKey] = docs
        void cache.deleteByType(type)
        acc.cacheItems.push(...(Object.values(toRaw(state[stateKey])) as { id: string }[]))
      } else {
        state[stateKey] = { ...state[stateKey], ...docs }
        acc.cacheItems.push(...(Object.values(docs) as { id: string }[]))
      }

      if (documentSet?.etag) {
        acc.etagsByType[type] = documentSet.etag
      }
      return acc
    },
    {
      cacheItems: [] as { id: string }[],
      etagsByType: {} as { [P in K]: string }
    }
  )

  Object.assign(etags, etagsByType)
  await cache.setItems(cacheItems)

  await ensureMultiplayerSessionDependencies()
  if ((types as EntityType[]).includes('course') && !state.vuplex?.features?.['prioritized-download-queue']) {
    registerModulesForTechniques()
  }
  initialUpdateResolve()
}

export interface DocumentSet<T extends Entity> {
  etag?: string
  documents: EntityDict<T>
  isAll: boolean
}

export type DocumentsByOrganizationResponse<T extends Record<string, Entity>> = {
  [K in keyof T]?: DocumentSet<T[K]>
}

export type EntityType = keyof typeof typeToStateKey

const typeToStateKey = {
  prebrief: 'prebriefs',
  course: 'courses',
  multiplayerSession: 'multiplayerSessions',
  userCoursePerformance: 'userCoursePerformances',
  playerTechnique: 'playerTechniques'
} as const
